import styled, { keyframes } from 'styled-components';

const fadeSlideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const NotificationOverlay = styled.div`
  position: fixed;
  inset: 0;
  @media (min-width: 768px) { left: var(--sidebar-width, 0px); }
  background: transparent;
  z-index: 999998;
`;

export const NotificationCenterContent = styled.div`
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 56px);
  right: 32px;
  width: 300px;
  max-height: min(480px, calc(100vh - 80px));
  border-radius: 16px;
  border: 1px solid #2b2d33;
  background: #23252a;
  box-shadow: 0px 8px 24px 0px #00000073;
  z-index: 999999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeSlideDown} 200ms ease-out;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 8px;
  flex-shrink: 0;
`;

export const PanelTitle = styled.div`
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  color: #e9ecf5;
`;

export const MarkAllReadButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;

  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #8fb0ff;

  &:hover {
    background: rgba(94, 132, 237, 0.1);
  }
  &:active {
    opacity: 0.7;
  }
`;

export const NotificationsList = styled.div`
  overflow-y: auto;
  padding: 0 16px;
  flex: 1;
  min-height: 0;

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.3s ease;

  &:hover {
    scrollbar-color: #3a3c44 transparent;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(58, 60, 68, 0);
    border-radius: 4px;
    transition: background 0.3s ease;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: #3a3c44;
  }
  &:hover::-webkit-scrollbar-thumb:hover {
    background: #4a4c54;
  }
`;

export const NotificationRow = styled.div<{ isFirst?: boolean; isUnread?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding-top: ${({ isFirst }) => (isFirst ? '8px' : '12px')};
  padding-bottom: 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 120ms ease;

  ${({ isUnread }) =>
    isUnread &&
    `
    position: relative;
  `}

  &:active {
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const NotificationIconWrapper = styled.div<{ isUnread?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 19.5px;
  background: ${({ isUnread }) => (isUnread ? '#2457e3' : '#3a3d44')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 400ms ease;
`;

export const UnreadDot = styled.span<{ $visible?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #5e84ed;
  flex-shrink: 0;
  margin-top: 6px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 400ms ease;
`;

export const NotificationTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

export const NotificationTitle = styled.div`
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #e9ecf5;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const NotificationTimestamp = styled.div`
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 140%;
  color: #7e8494;
  text-align: left;
`;

export const NotificationDescription = styled.div`
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 120%;
  color: #b7bdcb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EmptyStateText = styled.div`
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 120%;
  color: #7e8494;
  padding: 24px 16px;
  text-align: center;
`;

export const NotificationDivider = styled.div`
  width: 100%;
  border-top: 1px solid #2b2d33;
`;

export const LoadMoreSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
  color: #7e8494;
  font-size: 12px;
`;

export const FileDownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #8fb0ff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const AudioSeekerWrapper = styled.div`
  margin-top: 6px;
`;

export const SwipeableRowWrapper = styled.div<{ $dx: number; $dismissing: boolean }>`
  transition: ${({ $dismissing }) =>
    $dismissing ? 'transform 200ms ease-out, opacity 200ms ease-out' : 'none'};
  transform: translateX(${({ $dx, $dismissing }) => ($dismissing ? `${$dx > 0 ? 100 : -100}%` : `${$dx}px`)});
  opacity: ${({ $dismissing }) => ($dismissing ? 0 : 1)};
  will-change: transform, opacity;
  touch-action: pan-y;
`;

export const SwipeBg = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
`;

export const SwipeRowContainer = styled.div`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;
